import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Inbox, Users, FileEdit, KeyRound, ShieldCheck } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import EnquiriesPanel from "../../components/admin/EnquiriesPanel";
import SubscribersPanel from "../../components/admin/SubscribersPanel";
import ContentPanel from "../../components/admin/ContentPanel";
import ChangePasswordPanel from "../../components/admin/ChangePasswordPanel";

const TABS = [
  { key: "enquiries", label: "Enquiries", icon: Inbox },
  { key: "subscribers", label: "Subscribers", icon: Users },
  { key: "content", label: "Site Content", icon: FileEdit },
  { key: "password", label: "Password", icon: KeyRound },
];

export default function AdminDashboard() {
  const { admin, logout, refresh } = useAdminAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");

  const doLogout = () => { logout(); navigate("/admin"); };

  // Force password change on first login
  if (admin && admin.must_change_password) {
    return (
      <div className="min-h-screen bg-homi-light">
        <header className="bg-homi-black px-6 py-4 flex items-center justify-between">
          <span className="inline-flex items-center gap-2 text-white font-heading font-bold"><ShieldCheck className="w-5 h-5 text-homi-gold" /> EMB Admin</span>
          <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm"><LogOut className="w-4 h-4" /> Sign out</button>
        </header>
        <div className="max-w-3xl mx-auto px-6 py-12">
          <h1 className="font-heading font-extrabold text-2xl text-homi-black mb-2">Set your new password</h1>
          <p className="text-homi-body mb-8">Welcome! You're using a temporary password. Please choose a new one to secure your account.</p>
          <ChangePasswordPanel forced onDone={async () => { await refresh(); }} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-homi-light" data-testid="admin-dashboard">
      <header className="bg-homi-black px-6 py-4 flex items-center justify-between sticky top-0 z-20">
        <span className="inline-flex items-center gap-2 text-white font-heading font-bold"><ShieldCheck className="w-5 h-5 text-homi-gold" /> EMB Admin</span>
        <div className="flex items-center gap-4">
          <span className="hidden sm:inline text-white/50 text-sm">{admin?.email}</span>
          <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-heading font-semibold"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <nav className="flex gap-2 mb-8 overflow-x-auto pb-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-heading font-semibold text-sm whitespace-nowrap transition-colors ${tab === t.key ? "bg-homi-gold text-homi-black" : "bg-white text-homi-black border border-gray-200 hover:bg-homi-gold/10"}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </nav>

        {tab === "enquiries" && <EnquiriesPanel />}
        {tab === "subscribers" && <SubscribersPanel />}
        {tab === "content" && <ContentPanel />}
        {tab === "password" && (
          <div>
            <h2 className="font-heading font-extrabold text-xl text-homi-black mb-6">Change your password</h2>
            <ChangePasswordPanel />
          </div>
        )}
      </div>
    </div>
  );
}
